import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription } from 'rxjs';
import { ORGANISATION_API, RepositoryService } from '../linq-utils/repository.service';
import { InsightsRefreshService } from '../model-services/insights-refresh.service';
import { InsightsStatus, nextNotice, NoticeState } from './insights-status';

// how often the server is asked while the insights shown are being worked out again
const POLL_MS = 5000;

// A line under the analysis header while the insights shown are being worked out again after a change: waiting for
// them when this page asked, saying the figures may be out of date when it was asked for elsewhere (another tab,
// someone else, or a project still being worked out after the current state), and offering the new figures once ready
@Component({
    selector: 'app-insights-status',
    standalone: true,
    imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule],
    template: `
        @switch (state.notice) {
            @case ('local') {
                <div class="notice" role="status" data-testid="insights-status"><mat-spinner diameter="16"></mat-spinner>
                    <span>Updating the insights after your changes. The figures show once they're worked out.</span></div>
            }
            @case ('remote') {
                <div class="notice" role="status" data-testid="insights-status"><mat-spinner diameter="16"></mat-spinner>
                    <span>The insights are being updated after a change, so these figures may be out of date. The new ones will be offered here when they're ready.</span></div>
            }
            @case ('ready') {
                <div class="notice ready" role="status" data-testid="insights-status"><mat-icon>check_circle</mat-icon>
                    <span>Updated insights are ready.</span>
                    <button mat-stroked-button color="accent" type="button" (click)="show()" data-testid="insights-status-show">Show them</button>
                    <button mat-icon-button type="button" (click)="dismiss()" aria-label="Dismiss"><mat-icon>close</mat-icon></button>
                </div>
            }
        }
    `,
    styles: [`
        :host { display: block; }
        .notice { display: flex; align-items: center; gap: 10px; padding: 6px 16px; background: #fff8e1; border-bottom: 1px solid #f3e2a9; font-size: 13px; color: #5b4a12; }
        .notice.ready { background: var(--teal-soft, #e0f5f4); border-bottom-color: #b9e5e3; color: #0f4f4d; }
        .notice mat-icon { width: 18px; height: 18px; font-size: 18px; }
        .notice span { flex: 0 1 auto; }
    `],
})
export class InsightsStatusComponent implements OnInit, OnChanges, OnDestroy {
    @Input() organisationKey?: string;
    @Input() workspaceKey?: string;

    state: NoticeState = { notice: 'none', sawRemote: false };
    private status?: InsightsStatus;
    private sub?: Subscription;
    private timer?: number;
    private destroyed = false;

    constructor(private repoService: RepositoryService, private refreshes: InsightsRefreshService) {}

    ngOnInit() {
        // a refresh from this page starting or ending: say so, and ask what is left
        this.sub = this.refreshes.changed.subscribe(() => {
            this.update();
            if (!this.refreshes.isRefreshing(this.workspaceKey)) void this.check();
        });
    }

    ngOnChanges() {
        this.state = { notice: 'none', sawRemote: false };
        void this.check();
    }

    ngOnDestroy() {
        this.destroyed = true;
        this.sub?.unsubscribe();
        window.clearTimeout(this.timer);
    }

    // the new figures, by loading the page again
    show() {
        window.location.reload();
    }

    dismiss() {
        this.state = { notice: 'none', sawRemote: false };
    }

    private update() {
        if (this.workspaceKey) this.state = nextNotice(this.state, this.refreshes.isRefreshing(this.workspaceKey), this.status, this.workspaceKey);
    }

    // what the server says, and again shortly while the insights shown are being worked out
    private async check() {
        window.clearTimeout(this.timer);
        if (!this.organisationKey || !this.workspaceKey || this.destroyed) return;
        try {
            this.status = await this.repoService.callSingleAPI(this.repoService.getData<InsightsStatus>(`${ORGANISATION_API(this.organisationKey)}/insights/status`),
                'Insights status', 'Check the insights status', undefined, undefined, undefined, undefined, false);
        } catch {
            // a server without the status says nothing, rather than an error
            this.status = undefined;
        }
        if (this.destroyed) return;
        this.update();
        if (this.state.notice === 'remote' || this.refreshes.isRefreshing(this.workspaceKey)) this.timer = window.setTimeout(() => void this.check(), POLL_MS);
    }
}
